import React, { useMemo, useState } from 'react'
import useQuery from '@helpers/JIFI/useQuery'
import superagent from 'superagent'
import { backendUrl } from '@helpers/consts'

import Breadcrumbs from '@components/Common/Breadcrumb'
import { Row } from '@shadcn/components/ui/row'
import { Col } from '@shadcn/components/ui/col'
import { Card, CardContent as CardBody } from '@shadcn/components/ui/card'
import { Button } from '@shadcn/components/ui/button'
import { Input } from '@shadcn/components/ui/input'
import { useForm } from 'react-hook-form'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@shadcn/components/ui/select'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, TableSkeleton } from '@shadcn/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@shadcn/components/ui/dialog'
import { TablePagination } from '@components/supply-chain/TablePagination'
import { createColumnHelper, useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table'

const columnHelper = createColumnHelper()

// Keys are stored as typed (the backend only trims + lowercases) and live flags use hyphens,
// e.g. delayed-order-automation, so hyphens are allowed.
const normalizeKey = (k) => (k || '').toLowerCase().replace(/[^a-z0-9_-]/g, '_');



const FeatureFlagsPage = () => {
  const [user] = useState(JSON.parse(localStorage.getItem('authUser')) || {})
  const canManage = Array.isArray(user?.permissions) && user.permissions.includes('ManageFeatureFlags')
  

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  
  const [refreshTick, setRefreshTick] = useState(0)

  
  const [isCreateOpen, setCreateOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  

  
  const form = useForm({ defaultValues: { key: '', enabled: 'false', description: '' } })
  const { register, handleSubmit, reset, setValue, watch } = form
  
  
  

  const [savingKey, setSavingKey] = useState(null)

  const queryData = useMemo(() => ({ page, perPage }), [page, perPage])

  
  const [flagsResp, state] = useQuery({ url: '/featureflags/all', queryData, refresh: refreshTick })

  const data = useMemo(() => {
    if (!flagsResp) return { data: [], total: 0 }
  
    if (Array.isArray(flagsResp)) return { data: flagsResp, total: flagsResp.length }
    return { data: flagsResp.data || [], total: flagsResp.total || flagsResp.count || 0 }
  }, [flagsResp])

  const onRefresh = () => setRefreshTick((x) => x + 1)

  

  const columns = useMemo(
    () => [
      columnHelper.accessor('key', {
        header: () => 'Key',
        cell: ({ row }) => <span className="font-mono text-black">{row.original.key}</span>,
      }),
      columnHelper.accessor('enabled', {
        header: () => 'Enabled',
        cell: ({ row }) => (
          <span className="text-black">{String(row.original.enabled)}</span>
        ),
      }),
      columnHelper.accessor('description', {
        header: () => 'Description',
        cell: ({ row }) => (
          <span className="text-black">{row.original.description || '-'}</span>
        ),
      }),
   
      columnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        cell: ({ row }) => (
          <div className="flex gap-2">
            {canManage && (
              <Button variant="outline" size="sm" onClick={() => openEdit(row.original)}>
                Edit
              </Button>
            )}
          </div>
        ),
      }),
    ],
    [canManage]
  )

  

  const table = useReactTable({ data: data.data, columns, getCoreRowModel: getCoreRowModel() })

  const openCreate = () => {
    setIsEditMode(false)
    reset({ key: '', enabled: 'false', description: '' })
    setCreateOpen(true)
  }
  const openEdit = (row) => {
    setIsEditMode(true)
    reset({
      key: row.key,
      enabled: String(Boolean(row.enabled)),
      description: row.description || '',
    })
    setCreateOpen(true)
  }

  const validateForm = () => {
    const k = isEditMode ? watch('key') : normalizeKey(watch('key'))
    if (!k) return 'Key is required (lowercase letters/numbers/underscore/hyphen).'
    if (!isEditMode && k !== watch('key')) return 'Key must be lowercase letters/numbers/underscore/hyphen.'
    if (watch('enabled') === undefined) return 'Enabled must be set.'
    return null
  }

  const submitCreate = async () => {
    const err = validateForm()
    if (err) {
      alert(err)
      return
    }
    // Editing saves the row's existing key unchanged; normalising it created a new flag instead.
    const key = isEditMode ? watch('key') : normalizeKey(watch('key'))
    const body = {
      enabled: String(watch('enabled')) === 'true',
      description: watch('description') || "",
    }
    setSavingKey(key)
    try {
      await superagent
        .put(`${backendUrl}/featureflags/${encodeURIComponent(key)}`)
        .set('Authorization', `Bearer ${localStorage.getItem('authJWT')}`)
        .send(body)
      setCreateOpen(false)
      onRefresh()
    } catch (e) {
      console.error(e)
      alert('Save failed')
    } finally {
      setSavingKey(null)
    }
  }
      

  return (
    <div className="page-content">
      <div className="w-full px-3 mx-auto xl:max-w-[85%] pt-4">
        <Breadcrumbs title="Settings" breadcrumbItem={'Feature Flags'} />

        {/* Top Bar */}
        <Row className="mb-4 gap-4 items-end">
          <Col className="flex gap-2 w-full md:w-auto ml-auto justify-end">
            {canManage && <Button onClick={openCreate}>New Flag</Button>}
            <Button variant="outline" onClick={onRefresh}>Refresh</Button>
          </Col>
        </Row>

        <Card className="mb-6 pt-6">
          <CardBody>
            <div className="table-responsive rounded-lg border border-gray-200 bg-white text-black">
              <Table className="table-auto w-full">
                <TableHeader>
                  {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id} className="bg-black hover:bg-black">
                      {hg.headers.map((header) => (
                        <TableHead key={header.id} className="px-4 py-3 text-left text-xs font-medium text-white uppercase hover:bg-transparent">
                          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                {state === 'loading' ? (
                  <TableSkeleton columns={6} perPage={perPage} />
                ) : (
                  <TableBody>
                    {table.getRowModel().rows?.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id} className="hover:bg-gray-50">
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id} className="px-4 py-3 text-black">
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} className="px-4 py-6 text-center text-black">
                          No flags found
                        </TableCell>
                      </TableRow>
                    )}

                    
                  </TableBody>
                )}
              </Table>
            </div>

            {data.total > perPage && (
              <div className="mt-4">
                <TablePagination count={data.total} page={page} setPage={setPage} perPage={perPage} setPerPage={setPerPage} />
              </div>
            )}
          </CardBody>
        </Card>

        {/* Create/Edit Flag Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-[560px] bg-white text-black">
            <DialogHeader>
              <DialogTitle>{isEditMode ? 'Edit Flag' : 'New Flag'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit(submitCreate)} className="space-y-4 py-2">
              <div>
                <label className="block text-sm text-black mb-1">Key</label>
                <Input {...register('key')} readOnly={isEditMode} placeholder="enable_something" onChange={(e) => setValue('key', normalizeKey(e.target.value))} />
                <div className="text-xs text-gray-600 mt-1">lowercase letters/numbers/underscore/hyphen only</div>
              </div>
              <div>
                <label className="block text-sm text-black mb-1">Enabled</label>
                <Select value={watch('enabled') ?? ''} onValueChange={(v) => setValue('enabled', v)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select true/false" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">true</SelectItem>
                    <SelectItem value="false">false</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm text-black mb-1">Description (optional)</label>
                <Input {...register('description')} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={savingKey === watch('key')}>{savingKey ? 'Saving...' : 'Save'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}

export default FeatureFlagsPage
